import { parseCsvRows } from '@/modules/payroll/utils/csv.utils';

/** One report row keyed by CSV header. Columns vary by tenant integrations. */
export type PayRunReadinessRecord = Record<string, string>;

/** The most recent fully completed Monday–Sunday pay week. */
export const getLatestCompletedPayRunWeek = (
  now = new Date(),
): { start: Date; end: Date } => {
  const daysSinceSunday = now.getDay() === 0 ? 7 : now.getDay();
  const end = new Date(now);
  end.setHours(0, 0, 0, 0);
  end.setDate(end.getDate() - daysSinceSunday);
  const start = new Date(end);
  start.setDate(start.getDate() - 6);
  end.setHours(23, 59, 59, 999);

  return { start, end };
};

export const mapPayRunReadinessCsv = (
  csvText: string,
): { headers: string[]; records: PayRunReadinessRecord[] } => {
  const rows = parseCsvRows(csvText);
  if (rows.length === 0) {
    return { headers: [], records: [] };
  }

  const headers = rows[0];
  const records = rows.slice(1).map((cells) =>
    headers.reduce<PayRunReadinessRecord>((record, header, headerIndex) => {
      record[header] = cells[headerIndex] ?? '';
      return record;
    }, {}),
  );

  return { headers, records };
};

/**
 * The backend only emits Astute columns when the tenant has Astute enabled, so
 * the OID column doubles as the "integration available" signal.
 */
export const OCCUPATION_LIBRARY_OID_HEADER = 'astuteOccupationLibraryOid';
export const OCCUPATION_LIBRARY_NAME_HEADER = 'astuteOccupationLibraryName';

/**
 * Inserts the occupation library name next to its OID. Until the libraries
 * load the column stays blank; an OID with no matching library shows the OID.
 */
export const withOccupationLibraryColumn = (
  headers: string[],
  records: PayRunReadinessRecord[],
  labelByOid: Record<string, string> | null,
): { headers: string[]; records: PayRunReadinessRecord[] } => {
  const oidIndex = headers.indexOf(OCCUPATION_LIBRARY_OID_HEADER);
  if (oidIndex < 0) {
    return { headers, records };
  }

  return {
    headers: [
      ...headers.slice(0, oidIndex + 1),
      OCCUPATION_LIBRARY_NAME_HEADER,
      ...headers.slice(oidIndex + 1),
    ],
    records: records.map((record) => {
      const oid = record[OCCUPATION_LIBRARY_OID_HEADER];
      return {
        ...record,
        [OCCUPATION_LIBRARY_NAME_HEADER]:
          oid && labelByOid ? (labelByOid[oid] ?? oid) : '',
      };
    }),
  };
};
